import { UseQueryResult } from '@tanstack/react-query';
import { 
    useGetProcess, 
    useGetProcessesByFsp, 
    useGetAllProcesses
} from '@/shared/api/client/fsp-payment-processes';
import { FspPaymentProcessDTO, FspType, GetProcessParams } from '@/shared/api/model';

/**
 * Hook to fetch payment process configuration for a specific FSP and language
 * @param fspType
 * @param fspCode - The FSP code to fetch the process for
 * @param languageCode - The language code for the process (defaults to 'en')
 * @returns A tuple containing the query result and a refetch function
 */
export function useGetPaymentProcess(
    fspType: FspType,
    fspCode: string,
    languageCode: string = 'en'
): [UseQueryResult<FspPaymentProcessDTO | undefined, Error>, () => void] {
    // Use the Orval-generated hook
    // @ts-ignore

    const params: GetProcessParams = { fspCode, languageCode };
    
    const query = useGetProcess(params, {
        query: {
            // Only fetch if fspCode is provided
            enabled: !!fspCode && fspType === FspType.BANK,
            // Keep the data fresh for 1 minute
            staleTime: 60 * 1000,
            // Handle undefined response
            select: (data) => data || undefined,
        }
    });

    // Extract data from nested axios response and maintain the original interface
    // @ts-ignore
    const result = {
        ...query,
        data: query.data,
        error: query.error as Error
    } as UseQueryResult<FspPaymentProcessDTO | undefined, Error>;

    const refetch = () => query.refetch();

    return [result, refetch];
}

/**
 * Hook to fetch all payment processes for a specific FSP
 * @param fspCode - The FSP code to fetch processes for
 * @returns A tuple containing the query result and a refetch function
 */
export function useGetPaymentProcessesByFsp(
    fspCode: string
): [UseQueryResult<FspPaymentProcessDTO[], Error>, () => void] {
    // Use the Orval-generated hook
    const query = useGetProcessesByFsp(fspCode, {
        query: {
            enabled: !!fspCode
        }
    });

    // Extract data from nested axios response and maintain the original interface
    // @ts-ignore
    const result = {
        ...query,
        data: query.data,
        error: query.error as Error
    } as UseQueryResult<FspPaymentProcessDTO[], Error>;

    const refetch = () => query.refetch();

    return [result, refetch];
}

/**
 * Hook to fetch all payment processes
 * @returns A tuple containing the query result and a refetch function
 */
export function useGetAllPaymentProcesses(): [UseQueryResult<FspPaymentProcessDTO[], Error>, () => void] {
    // Use the Orval-generated hook
    const query = useGetAllProcesses({
        query: {
            staleTime: 60 * 1000,
        }
    });

    // Extract data from nested axios response and maintain the original interface
    // @ts-ignore
    const result = {
        ...query,
        data: query.data,
        error: query.error as Error
    } as UseQueryResult<FspPaymentProcessDTO[], Error>;

    const refetch = () => query.refetch();

    return [result, refetch];
}